import {
  ChevronDown,
  LogOut,
} from "lucide-react";
import { cn } from "@/lib/utils";
import cookies from "js-cookie";
import { cookieNames } from "@monorepo/common";
import { Breadcrumbs } from "./breadcrumbs";
import { useAuthStore } from "@/stores/auth-store";
import { useGetPersonalInfo, useGetPublicProfile, useGetUserDetails } from "@/hooks/use-user-profile";
import { useState, useEffect, useRef } from "react";

interface HeaderProps {
  className?: string;
}

export function Header({ className }: HeaderProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isAuth, checkAuth } = useAuthStore();
  const { data: userData } = useGetPersonalInfo(isAuth);
  const { data: publicData } = useGetPublicProfile(isAuth);
  const { data: userDetailsData } = useGetUserDetails(isAuth);

  // Check auth status on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const { logout: zustandLogout } = useAuthStore();

  const logOut = () => {
    zustandLogout();
    cookies.remove(cookieNames.refresh_token);
    if (process.env.NODE_ENV === 'production') {
      window.location.href = 'https://auth.cpgmedia.com/logout?returnUrl=https://account.bevnet.com/';
      return;
    }
    window.location.reload();
  };

  // Get user initials for avatar fallback
  const userInitials = userData?.firstName && userData?.lastName
    ? `${userData.firstName[0]}${userData.lastName[0]}`.toUpperCase()
    : "??";

  const userName = userData?.firstName && userData?.lastName
    ? `${userData.firstName} ${userData.lastName}`
    : "Unknown User";
  const userEmail = userData?.email ?? "";
  const userPicture = publicData?.headshot;

  // Close dropdown on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && dropdownOpen) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [dropdownOpen]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [dropdownOpen]);

  return (
    <header
      className={cn(
        "sticky top-0 z-30 flex h-14 items-center justify-between",
        "border-b border-border bg-background/95 backdrop-blur",
        "px-4 lg:px-6",
        className
      )}
    >
      {/* Left side - Breadcrumbs */}
      <div className="flex items-center gap-4">
        <Breadcrumbs />
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center gap-1">
        {/* User Menu */}
        {isAuth && (
          <div className="relative" ref={dropdownRef}>
            <button
              id="user-menu-button"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={cn(
                "flex items-center gap-2 rounded-md",
                "h-9 px-2 text-sm font-medium",
                "bg-transparent hover:bg-accent",
                "text-foreground/80 hover:text-foreground",
                "transition-colors duration-150",
                "focus-visible:outline-none focus-visible:ring-2",
                "focus-visible:ring-ring focus-visible:ring-offset-2"
              )}
              aria-label="User menu"
              aria-haspopup="true"
              aria-expanded={dropdownOpen}
            >
              {/* Avatar */}
              {userPicture ? (
                <img
                  src={userPicture}
                  alt={userName}
                  className="h-7 w-7 rounded-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                  {userInitials}
                </div>
              )}
              <span className="hidden md:inline">{userName}</span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </button>

            {/* Dropdown Menu */}
            {dropdownOpen && (
              <div
                className={cn(
                  "absolute right-0 top-full mt-1 w-56",
                  "rounded-md border border-border bg-popover p-1",
                  "shadow-lg",
                  "transition-all duration-150",
                  "z-50"
                )}
                role="menu"
                aria-labelledby="user-menu-button"
              >
              <div className="flex items-center gap-3 px-2 py-2">
                {userPicture ? (
                  <img
                    src={userPicture}
                    alt={userName}
                    className="h-10 w-10 rounded-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-semibold">
                    {userInitials}
                  </div>
                )}
                <div className="flex flex-col">
                  <span className="font-medium text-sm">{userName}</span>
                  {userEmail && (
                    <div className="text-muted-foreground text-xs">{userEmail}</div>
                  )}
                </div>
              </div>

              {userDetailsData?.subscriptionTypes && userDetailsData.subscriptionTypes.length > 0 && (
                <div className="px-2 py-2 border-t border-border">
                  <div className="text-xs font-medium text-muted-foreground mb-1">
                    Subscription:
                  </div>
                  <div className="text-sm font-medium text-destructive">
                    {userDetailsData.subscriptionTypes.join(' - ')}
                  </div>
                </div>
              )}

              <div className="h-px bg-border my-1" />

              <button
                onClick={logOut}
                className={cn(
                  "flex w-full items-center gap-2 rounded-sm px-2 py-1.5",
                  "text-sm text-destructive",
                  "hover:bg-destructive/10",
                  "transition-colors duration-150"
                )}
                role="menuitem"
              >
                <LogOut className="h-4 w-4" />
                Log out
              </button>
            </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
